import styled from '@emotion/styled';
import React, {
  MutableRefObject,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { StyledText } from './styled-components';

const StyledContainer = styled('form')(
  ({ theme, useInlineStyles }: { theme?: any; useInlineStyles?: boolean }) => ({
    position: 'relative',
    ...(useInlineStyles && {
      display: 'inline-block',
    }),
  })
);

const StyledContentContainer = styled('div')(
  ({
    theme,
    maxWidth,
    expanded,
    useInlineStyles,
  }: {
    theme?: any;
    maxWidth?: number;
    useInlineStyles?: boolean;
    expanded: boolean;
  }) => ({
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'center',
    padding: '7.5px 8px',
    height: '32px',
    width: `${maxWidth || '120'}px`,
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: '4px',
    '&::after': {
      display: 'inline-block',
      content: '""',
      background: 'no-repeat url("assets/dropdown-arrow.svg") center',
      width: '8px',
      height: '8px',
      marginLeft: 'auto',
      ...(expanded && {
        transform: 'rotate(180deg)',
      }),
    },
    ...(useInlineStyles && {
      padding: '0',
      border: 'none',
      width: 'fit-content',
      '&::after': {
        display: 'none',
      },
    }),
  })
);

const StyledChildrenContainer = styled('div')(
  ({ theme, useInlineStyles }: { theme?: any; useInlineStyles?: boolean }) => ({
    position: 'absolute',
    zIndex: '10000',
    top: useInlineStyles ? '40px' : '48px',
    left: 0,
    width: 'fit-content',
    padding: '16px',
    background: '#FFFFFF',
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: '4px',
  })
);

interface Props {
  maxWidth?: number;
  useInlineStyles?: boolean;
  displayText: string;
  children: ReactNode;
}

export const DropdownContainer = ({
  maxWidth,
  displayText,
  children,
  useInlineStyles,
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLFormElement>();

  const handleClick = useCallback(
    (event?: React.MouseEvent<HTMLDivElement>) => {
      event?.stopPropagation();
      setExpanded((value) => !value);
    },
    []
  );

  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      if (expanded && !containerRef?.current?.contains(event.target as Node)) {
        setExpanded(false);
      }
    },
    [expanded]
  );

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [handleOutsideClick]);

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    handleClick();
  };

  return (
    <StyledContainer
      onSubmit={handleSubmit}
      ref={containerRef as MutableRefObject<HTMLFormElement>}
      useInlineStyles={useInlineStyles}
    >
      <StyledContentContainer
        maxWidth={maxWidth}
        expanded={expanded}
        onClick={handleClick}
        useInlineStyles={useInlineStyles}
      >
        <StyledText bold={useInlineStyles} isSmall>
          {displayText}
        </StyledText>
      </StyledContentContainer>
      {expanded && (
        <StyledChildrenContainer
          onClick={(event) => event.stopPropagation()}
          useInlineStyles={useInlineStyles}
        >
          {children}
        </StyledChildrenContainer>
      )}
    </StyledContainer>
  );
};
